<template>
  <div class="row rounded shadow">
    <div class="col-lg-4 p-3 p-md-4 bg-white" v-if="election">
      <div v-if="organisationBranding.name" class="mb-3 mt-n3 mt-md-n4 mx-n3 mx-md-n4 p-1 bg-secondary text-center">
        <span class="sub-lead"
          ><b>{{ organisationBranding.name }}</b></span
        >
      </div>
      <LocaleSwitcher class="mb-3" />
      <img
        v-if="election.theme.logo"
        :src="election.theme.logo"
        :alt="$t('js.accessibility.logo')"
        class="mb-3"
        style="max-height: 100px; max-width: 100%"
      />
      <div class="h3">{{ election.title[$i18n.locale] }}</div>
      <div class="lead" v-html="election.description[$i18n.locale]"></div>
    </div>

    <div v-if="!electionRunning" id="main_content" class="col-lg-8 p-3 p-md-4 bg-theme d-flex flex-column justify-content-center">
      <h3>{{ openingFuture() ? $t("js.election_client.election_status.not_yet_open") : $t("js.election_client.election_status.closed") }}</h3>
      <p v-if="openingFuture()"
        v-html="
          $t(openingFutureTranslationKey(), {
            from: $d(new Date(election.schedule.enableAt), 'long'),
            to: $d(new Date(election.schedule.disableAt), 'long'),
          })
        "
      />

      <p v-if='closedPast()'
        v-html="
          $t(closedPastTranslationKey(), {
            from: $d(new Date(election.schedule.enableAt), 'long'),
            to: $d(new Date(election.schedule.disableAt), 'long'),
          })
        "
      />
    </div>
    <div v-else id="main_content" class="col-lg-8 p-3 p-md-4 bg-theme d-flex flex-column justify-content-center">
      <h4>{{ $t("js.authenticate.sign_in_header") }}</h4>
      <form
        class="form"
        @submit.prevent="attemptLogin({ electionCodes: electionCodes.map((ec: ElectionCode) => electionCodeValues[ec.id])})"
        autocomplete="off"
      >
        <div class="mb-3" v-for="(electionCode, index) in electionCodes">
          <label :for="`election_code_${index}`" class="form-label">{{ electionCode.label[$i18n.locale] }}</label>
          <form
            @submit.prevent="attemptLogin({ electionCodes: electionCodes.map((ec: ElectionCode) => electionCodeValues[ec.id])})"
            autocomplete="off"
            class="input-group password-input-group"
            :aria-label="electionCode.label[$i18n.locale]"
          >
            <input
              :id="`election_code_${index}`"
              :type="showPassword[electionCode.id] ? 'text' : 'password'"
              class="form-control"
              v-model="electionCodeValues[electionCode.id]"
              autocomplete="off"
              :autofocus="index === 0 || null"
              :placeholder="electionCode.placeholder[$i18n.locale]"
              value=""
            />
            <div
              :aria-checked="showPassword[electionCode.id] || 'false'"
              :aria-label="$t('js.authenticate.show_password')"
              class="btn password-toggle"
              role="checkbox"
              @click="showPassword[electionCode.id] = !showPassword[electionCode.id]"
            >
              <AVIcon :icon="showPassword[electionCode.id] ? 'eye' : 'eye-slash'" />
            </div>
          </form>
          <small>{{ electionCode.help[$i18n.locale] }}</small>
        </div>
        <transition name="fade" mode="out-in">
          <div class="p-3 mb-3 bg-theme-danger" v-if="error">
            <span class="float-end" role="button" @click="dismissError()" :aria-label="$t('js.actions.close')">
              <AVIcon icon="xmark" />
            </span>
            {{ errorMessage }}
          </div>
        </transition>

        <div class="mb-3 d-flex justify-content-end">
          <button type="submit" :disabled="!canLogin" class="btn btn-outline-contrast rounded-0">
            <span v-if="waitingForResponse" class="fas fa-spin fa-spinner"></span>
            {{ $t("js.authenticate.sign_in_button") }}
          </button>
        </div>
      </form>
      <a v-if="externalSignInUrl" :href="externalSignInUrl" class="btn btn-lg rounded-0 btn-outline-contrast">
        {{ $t("js.authenticate.go_to_sign_in_button") }}
      </a>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { mapActions, mapState } from "pinia";
import LocaleSwitcher from "../shared/LocaleSwitcher.vue";
import { useVotingSessionStore } from "@/entrypoints/stores/voting_session";
import { useFrontendThemeStore } from "@/entrypoints/stores/frontend_theme";
import { useSharedStore } from "@/entrypoints/stores/shared";
import type { ElectionCode } from "@/types";
import type { LoginsApiClientParams } from "@/lib/voting/loginsApiClient";

export default defineComponent({
  components: { LocaleSwitcher },
  data() {
    return {
      error: null,
      electionCodeValues: {},
      showPassword: { 1: false, 2: false, 3: false },
      waitingForResponse: false,
    };
  },
  methods: {
    ...mapActions(useVotingSessionStore, ["authenticateByElectionCodes", "authenticateByLoginsClient"]),
    async attemptLogin({ electionCodes, loginsApiClientParams }: { electionCodes?: string[], loginsApiClientParams?: LoginsApiClientParams }) {
      if (!this.canLogin && !loginsApiClientParams) return console.debug("cant login");

      this.error = null;

      this.waitingForResponse = true;
      if (electionCodes) {
        //strip leading and trailing whitespace to protect against copy paste errors
        this.error = await this.authenticateByElectionCodes(electionCodes.map((e_code) => e_code.trim()), this.election.curveName);
      } else if (loginsApiClientParams) {
        this.error = await this.authenticateByLoginsClient(loginsApiClientParams);
      } else {
        console.debug("I got nothing!", electionCodes, loginsApiClientParams);
      }
      this.waitingForResponse = false;
    },
    dismissError() {
      this.error = null;
    },
    openingFuture() {
      if (this.election.status !== "scheduled" || !this.election.schedule.enableAt) return false;
      const enableAt = Date.parse(this.election.schedule.enableAt);
      const now = Date.now();

      return enableAt > now;
    },
    closedPast() {
      if (this.election.status !== "scheduled" || !this.election.schedule.disableAt) return false;
      const disableAt = Date.parse(this.election.schedule.disableAt);
      const now = Date.now();

      return disableAt < now;
    },
    openingFutureTranslationKey() {
      return this.election.schedule.disableAt ? 'js.election_client.election_status.is_running_from_to_html' : 'js.election_client.election_status.opens_at_html'
    },
    closedPastTranslationKey() {
      return this.election.schedule.enableAt ? 'js.election_client.election_status.was_running_from_to_html' : 'js.election_client.election_status.closed_at_html'
    }
  },
  computed: {
    ...mapState(useSharedStore, ["election"]),
    ...mapState(useFrontendThemeStore, ["organisationBranding"]),
    errorMessage() {
      switch (this.error) {
        case "login_failed_voter_not_found":
          return this.$t("js.authenticate.errors.login_failed_voter_not_found");
        case "demo_voter_in_prod":
          return this.$t("js.authenticate.errors.login_failed_voter_not_found");
        case "login_failed_voter_disabled":
          return this.$t("js.authenticate.errors.login_failed_voter_disabled");
        case "login_failed_sign_up_required":
          return this.$t("js.authenticate.errors.login_failed_sign_up_required");
        case "login_failed_signature_invalid":
          return this.$t("js.authenticate.errors.login_failed_signature_invalid");
        case "election_not_running":
          return this.$t("js.authenticate.errors.election_not_running");
        default:
          return this.$t("js.authenticate.errors.login_failed");
      }
    },
    electionCodes() {
      return this.election.electionCodeTexts;
    },
    canLogin() {
      return (
        !this.waitingForResponse &&
        this.electionCodes.every((code: ElectionCode) => {
          return this.electionCodeValues[code.id];
        }, this)
      );
    },
    scheduleState() {
      const today = new Date();
      const enableAt = new Date(this.election.schedule.enableAt);
      return today <= enableAt;
    },
    electionRunning() {
      if (this.election.status === "open") return true;
      if (this.election.status === "closed") return false;
      if (this.election.status === "scheduled") return !this.openingFuture() && !this.closedPast();
      return true;
    },
    externalSignInUrl() {
      if (!["both", "external"].includes(this.election.redirect.sign_in_method) || !this.election.redirect.external_sign_in_url) return;

      return this.election.redirect.external_sign_in_url[this.$i18n.locale] || Object.values(this.election.redirect.external_sign_in_url)[0];
    },
  },
  mounted() {
    this.election.electionCodeTexts.forEach((ec: ElectionCode) =>
      this.showPassword[ec.id] = ec.visibility === "visible");

    if (window.location.hash.includes("session_token=")) {
      const url = new URL(window.location.toString());
      history.replaceState({}, "", location.pathname);
      url.search = url.hash.slice(1) // put hash without # into search for easier parsing
      const searchParams = url.searchParams; // TODO: Store for later use
      const sessionToken = searchParams.get("session_token");
      const hubSlug = searchParams.get("hub_slug");
      const loginsUrl = searchParams.get("logins_url");
      console.debug(loginsUrl)
      // extract slug from loginsUrl.pathname
      const loginsApiClientParams = { sessionToken, hubSlug, loginsUrl };
      this.attemptLogin({ loginsApiClientParams });
    } else if (window.location.hash) {
      let segments = location.hash.slice(1).split(",");
      history.replaceState({}, "", location.pathname);
      let direct = false;
      if (segments[0] === "d") {
        segments = segments.slice(1);
        direct = true;
      }
      const codes = new Map();
      for (let segment of segments) {
        const match = segment.match(/^([123]):([a-zA-Z0-9_]+)$/);
        if (match !== null) {
          const [_, num, inputValue] = match;
          codes.set(num, inputValue);
        }
      }
      this.electionCodeValues = Array.from(codes.entries()).reduce(
        (main, [key, value]) => ({
          ...main,
          [key]: value,
        }),
        {},
      );
      if (direct) {
        this.attemptLogin({ electionCodes: [...codes.values()]});
      }
    } else if (this.election.redirect.sign_in_method === 'external') {
      window.location.href = this.externalSignInUrl;
    }
  },
});
</script>
